import type {
  DebitedAccount, DirectDebitSituation, OpenRejection, OverdueInstallment, PaymentMethod, PaymentRequest, PaymentRequestStatus,
} from "../types/directDebit.types";
import type { OpenTransfer, PaymentOfferState, PaymentSnapshot } from "../types/unpaidInvoices.types";

/** Fastest to slowest: the CSE should be nudged toward the method that restores access first. */
export const UNPAID_METHOD_ORDER: PaymentMethod[] = ["card", "bank_transfer", "sepa_debit"];

export const UNPAID_METHOD_DELAY: Record<PaymentMethod, string> = {
  card: "Immédiat",
  bank_transfer: "Instantané si votre banque le propose, sinon 1 à 2 jours ouvrés",
  sepa_debit: "Environ 6 jours ouvrés",
};

/** The quicker the method restores access, the greener its badge. */
export const UNPAID_METHOD_SPEED_VARIANT: Record<PaymentMethod, "success" | "outline" | "warning"> = {
  card: "success",
  bank_transfer: "outline",
  sepa_debit: "warning",
};

export const UNPAID_METHOD_ACCESS_RESTORATION: Record<PaymentMethod, string> = {
  card: "L'accès est rétabli dès le paiement validé.",
  bank_transfer: "L'accès est rétabli dès réception : immédiatement avec un virement instantané, sinon sous 1 à 2 jours ouvrés.",
  sepa_debit: "Plus long : l'accès restera en pause jusqu'à réception du prélèvement, soit environ 6 jours ouvrés. Pour un accès immédiat, préférez la carte.",
};

type UnpaidMethodAvailability = {
  publishableKey: string | null
  /** A debit needs a mandate Stripe holds on the account. */
  canDebit: boolean
};

export const isMethodAvailable = (method: PaymentMethod, { publishableKey, canDebit }: UnpaidMethodAvailability): boolean => {
  if (method === "card") return Boolean(publishableKey);
  if (method === "sepa_debit") return canDebit;
  return true;
};

export const listUnpaidMethods = (availability: UnpaidMethodAvailability): PaymentMethod[] =>
  UNPAID_METHOD_ORDER.filter((method) => isMethodAvailable(method, availability));

type UnpaidRejectionsSummary = {
  payableRejectionIds: number[]
  payableInstallmentIds: number[]
  payableCents: number
  /** Open rejections a payment request already covers: unsettled, so they still count as owed. */
  pendingCents: number
};

const sumAmountCents = (rows: { amount_cents: number | null }[]): number =>
  rows.reduce((total, row) => total + (row.amount_cents ?? 0), 0);

/** What is owed: the open rejections, and the instalments past their day never rejected, each paid once. */
export const summarizeUnpaidRejections = (
  rejections: OpenRejection[] | undefined,
  overdue: OverdueInstallment[] = [],
): UnpaidRejectionsSummary => {
  const open = rejections ?? [];
  const payable = open.filter((rejection) => !rejection.being_paid);
  const pending = open.filter((rejection) => rejection.being_paid);
  const payableOverdue = overdue.filter((installment) => !installment.being_paid);
  const pendingOverdue = overdue.filter((installment) => installment.being_paid);

  return {
    payableRejectionIds: payable.map((rejection) => rejection.id),
    payableInstallmentIds: payableOverdue.map((installment) => installment.id),
    payableCents: sumAmountCents(payable) + sumAmountCents(payableOverdue),
    pendingCents: sumAmountCents(pending) + sumAmountCents(pendingOverdue),
  };
};

export const hasStripeMandate = (accounts: DebitedAccount[]): boolean =>
  accounts.some((account) => account.mandate?.at_stripe);

/** A card request the CSE can still pay: its rejections are locked until it is paid or expires. */
export const findOpenCardRequest = (situation: DirectDebitSituation | undefined): PaymentRequest | null =>
  situation?.payment_requests.find((request) => (
    request.payment_method === "card" && request.status === "awaiting_payment" && Boolean(request.client_secret)
  )) ?? null;

type PaymentOffer = Pick<PaymentSnapshot, "rejectionIds" | "installmentIds" | "totalCents" | "resumeClientSecret">;

/** What "Régler" starts: the rejections still to pay, else the open card request to pay again. */
export const buildPaymentOffer = (
  payableRejectionIds: number[],
  payableCents: number,
  openCardRequest: PaymentRequest | null,
  payableInstallmentIds: number[] = [],
): PaymentOffer | undefined => {
  if (payableCents > 0) {
    return {
      rejectionIds: payableRejectionIds, installmentIds: payableInstallmentIds, totalCents: payableCents, resumeClientSecret: null,
    };
  }
  if (!openCardRequest) return undefined;

  return {
    rejectionIds: openCardRequest.rejection_ids,
    installmentIds: openCardRequest.installment_ids ?? [],
    totalCents: openCardRequest.amount_cents ?? 0,
    resumeClientSecret: openCardRequest.client_secret,
  };
};

type SituationQueryState = { hasData: boolean, hasFailed: boolean, isFetching: boolean };

// A situation already read stays usable when a later read fails: the offer it gives is still right.
// React Query puts a query that never succeeded back to pending on a new attempt, hence `hasFailed`.
export const resolveOfferState = ({ hasData, hasFailed, isFetching }: SituationQueryState): PaymentOfferState => {
  if (hasData) return "ready";
  if (hasFailed) return isFetching ? "retrying" : "failed";
  return "loading";
};

const AWAITED_TRANSFER_STATUSES: PaymentRequestStatus[] = ["awaiting_payment", "partially_paid"];

/** A transfer the CRM answered and the CSE has not fully sent: its IBAN and reference can be shown again. */
export const findOpenTransfer = (situation: DirectDebitSituation | undefined): OpenTransfer | null => {
  const request = situation?.payment_requests.find((candidate) => (
    candidate.payment_method === "bank_transfer" && AWAITED_TRANSFER_STATUSES.includes(candidate.status)
  ));
  if (!request?.transfer) return null;
  return { transfer: request.transfer, receivedCents: request.received_cents };
};
